import {
  Box,
  makeStyles
} from "@material-ui/core";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import SearchBar from "../../../common/Search";
import { GuideContinueCard, GuideMenu } from "../../../common/GuideUI";
import { GuideSpotlight, useGuidedTour } from "../../../common/GuidedTour";
import { isGuideDone, markGuideDone, takeGuideRequest, readGuideProgress, saveGuideProgress } from "../../../common/guides";
import { pageInfoData } from "../../../common/pageInfoData";
import { SpotlightOverlay } from "../../../common/SpotlightOverlay";
import CustomTabs from "../../../common/Tabs";
import SaveCargoLabel from "../../../components/Calculator/SaveCargoLabel";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import CustomConfirmModal from "../../../components/Common/CustomConfirmModal";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import {
  apiPut,
  getCurrentUserOrganizationId,
  toastError,
  toastSuccess,
} from "../../../utils";
import * as calculatorActions from "../Calculator/actions";
import AddNewOrder, { CargoAIModal } from "./AddNewOrder";
import { LuSparkles } from "react-icons/lu";
import { BsPlusLg } from "react-icons/bs";
import AllOrdersTable from "./AllOrdersTable";
import CreateCargoAnalysis from "./CreateCargoAnalysis";
import CreateNewCargo from "./CreateNewCargo";
import { exportCargoPdf } from "./cargoRowPdf";
import { usePdfShare } from "../../../util/pdfFile";
import MyOrdersTable from "./MyOrdersTable";
import VesselList from "./VesselList";
import * as actions from "./actions";
import CreateCargoContractModel from "./createCargoContractModel";

const TOUR_STEPS = (setOpen) => [
  {
    key: "create",
    target: '[data-spotlight="add-cargo-btn"]',
    message: "Welcome! Let's start by creating a new order.",
    position: "auto",
    onNextAction: () => setOpen(true),
  },
  {
    key: "add-ports",
    target: '[data-spotlight="add-port-btn"]',
    message: "Great! Now add your loading and discharging ports here.",
    position: "auto",
  },
  {
    key: "upload-pdf",
    target: '[data-spotlight="upload-cargo-pdf-btn"]',
    message: "Alternatively, you can upload a PDF or CSV to auto-fill these details. Tip: You can download a PDF from an existing order and upload it here.",
    position: "auto",
  },
  {
    key: "create-ai",
    target: '[data-spotlight="create-ai-btn"]',
    message: "Or click Create with AI and paste your cargo details as text. AI fills in the order for you.",
    position: "auto",
  },
  {
    key: "export-ai-pdf",
    target: '[data-spotlight="export-ai-pdf-btn"]',
    message: "Need help generating an AI import file? Click here to download the AI Instruction PDF. You can upload this PDF along with your raw shipping documents to AI like ChatGPT or Claude. The AI will follow the instructions to generate and return a structured CSV file that you can upload here to auto-fill all cargo details!",
    position: "auto",
  },
  {
    key: "upload-attachment",
    target: '[data-spotlight="upload-attachment-btn"]',
    message: "You can also attach relevant files or images here.",
    position: "auto",
  },
  {
    key: "save",
    target: '[data-spotlight="save-cargo-btn"]',
    message: "Once you're done, click here to save your order.",
    invalidMessage: "Fill in the required details (name, laycan, ports and rates), then click here to save your order.",
    position: "auto",
    actionRequired: true,
  },
];

// "Show a cargo in the matrix": walk the stepper of the first order that is not in the matrix yet.
// Each step is done by clicking the highlighted element; the last step waits for the save to finish.
const MATRIX_STEPS = [
  {
    target: '[data-spotlight^="stepper-btn-"]',
    message: "Add Cargo to Matrix: click here to complete the cargo details.",
    capture: (el) => ({ orderId: el.getAttribute("data-spotlight").replace("stepper-btn-", "") }),
    waitMs: 30000,
  },
  {
    target: '[data-spotlight="wizard-fuel-prices"]',
    message: "Step 1 of 3: enter your fuel prices here first.",
    info: true,
    interactive: true,
    position: "left",
    waitMs: 15000,
  },
  {
    target: '[data-spotlight="wizard-next-btn"]',
    message: "Once the fuel prices are added, click Next.",
  },
  {
    target: '[data-spotlight="wizard-revenue-expenses"]',
    message: "Step 2 of 3: enter your revenue and expenses here first.",
    info: true,
    interactive: true,
    position: "left",
  },
  {
    target: '[data-spotlight="wizard-next-btn"]',
    message: "Once the revenue and expenses are added, click Next.",
  },
  {
    target: '[data-spotlight="wizard-port-ops"]',
    message: "Step 3 of 3: enter the extra port details here first.",
    info: true,
    interactive: true,
    position: "top",
  },
  {
    target: '[data-spotlight="wizard-next-btn"]',
    message: "Once the details are added, click Finish and confirm.",
  },
  {
    target: (ctx) => `[data-spotlight="show-in-matrix-btn"][data-order-id="${ctx.orderId}"]`,
    message: "Add Cargo to Matrix: turn this switch on to include your cargo.",
    actionRequired: true,
    waitMs: 120000,
  },
];

const useStyles = makeStyles({
  root: {
    width: "fit-content",
  },
});

const initialSearchType = {
  type: "",
  name: "",
  firstLoadPort: "",
  lastDischargePort: "",
  cargo: "",
  loadQty: "",
  voyType: "",
  account: "",
  createdDate: "",
  createdUserName: "",
};

const initialContractFields = {
  cargoFixtureId: '',
  contractName: '',
  cpDate: moment().format('YYYY-MM-DD'),
  counterpartyFullStyle: '',
  bankingDetails: '',
  contractingCompany: '',
  department: '',
  tradeArea: '',
  vesselType: '',
  minQty: '',
  maxQty: '',
  freightRate: '',
  currency: '',
  shipmentNo: '',
  charteringPIC: '',
  opsCoordinator: '',
  comments: '',
  loadingPortDemurrage: '',
  loadingPortDespatch: '',
  dischargingPortDemurrage: '',
  dischargingPortDespatch: '',
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function Orders(props) {
  const [cargoAiOpen, setCargoAiOpen] = useState(false);
  const { prepareShare, shareDialog } = usePdfShare();
  const { loader, setLoader = () => { } } = props;
  const dispatch = useDispatch();
  const {
    allClientOrders = [],
    allOrganizationOrders = [],
    queryOrderList = [],
    allClientOrdersPagination = {},
    allOrganizationOrdersPagination = {},
  } = useSelector((state) => state.orders) || {};
  const formErrors = useSelector((state) => state.common?.errorList) || [];
  const organizationId = getCurrentUserOrganizationId();
  const [value, setValue] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState({});
  const [selectedPorts, setSelectedPorts] = useState([]);
  const [selectedPortsCommence, setSelectedPortsCommence] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isStepper, setIsStepper] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [searchType, setSearchType] = useState({ ...initialSearchType });
  const [myFilters, setMyFilters] = useState({ sortBy: "createdAt", sortDirection: "desc", ...allClientOrdersPagination });
  const [allFilters, setAllFilters] = useState({ sortBy: "createdAt", sortDirection: "desc", ...allOrganizationOrdersPagination });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState({});
  const [isShareConfirm, setShareConfirm] = useState(false);
  const [shareLabel, setShareLabel] = useState("");
  const [saveLabel, setSaveLabel] = useState("");
  const [saveModal, setSaveModal] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [formData, setFormData] = useState(initialContractFields);
  const [textContract, setTextContract] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [saveLabelCargoVessel, setSaveLabelCargoVessel] = useState("");
  const [saveLabelCargoVesselError, setSaveLabelCargoVesselError] = useState(false);
  const [saveModalCargoVessel, setSaveModalCargoVessel] = useState(false);
  const [selectedCargoVessel, setSelectedCargoVessel] = useState(null);
  const [modelVessel, setModelVessel] = useState(false);
  const [vesselFilters, setVesselFilters] = useState({
    offset: 1,
    limit: 50,
  });
  const [fleets, setFleets] = useState([]);
  const [hasFetchedFleets, setHasFetchedFleets] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState({});
  const [isCargoAnalysisStepper, setCargoAnalysisStepper] = useState(false);
  const [modelCargoContract, setModelCargoContract] = useState(false);
  const [analysisVessel, setAnalysisVessel] = useState([]);
  const [cargoAnalysisData, setCargoAnalysisData] = useState({});
  const [selectedCargoForContract, setSelectedCargoForContract] = useState({});
  const [warningPopup, setWarningPopup] = useState(false);
  const [warningPopupForCopy, setWarningPopupForCopy] = useState(false);

  // --- Tour Persistence Helpers ---
  const getTourState = () => readGuideProgress("orders");
  const saveTourState = (updates) => saveGuideProgress("orders", updates);

  // --- Tour State ---
  const [tourStep, setTourStep] = useState(() => Math.min(getTourState().step || 0, 6));
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isTargetReady, setIsTargetReady] = useState(false);
  const [isPaused, setIsPaused] = useState(() => !!getTourState().paused);
  const [isWaitingForCompletion, setIsWaitingForCompletion] = useState(false);
  const [isTourCompleted, setIsTourCompleted] = useState(() => isGuideDone("cargo"));

  const steps = TOUR_STEPS(setOpen);

  // Tour State Initialization & Migration
  useEffect(() => {
    const state = getTourState();

    const savedStep = Math.min(state.step || 0, steps.length - 1);

    const authData = JSON.parse(localStorage.getItem("auth") || "{}");
    const visitedRoutes = authData.visitedRoutes || [];
    const route = window.location.pathname + window.location.search;
    const willShowPageInfo = (!!pageInfoData[route] && !visitedRoutes.includes(route)) || window.isPageInfoModalScheduled || window.isPageInfoModalOpen;

    if (!isTourCompleted) {
      setTourStep(savedStep);
      if (savedStep > 0 && !state.dismissed) setOpen(true);
      if (willShowPageInfo) {
        setIsTooltipVisible(false);
        setIsPaused(true);
      } else if (!state.paused) {
        setIsTooltipVisible(true);
        setIsPaused(false);
      }
    }
  }, [isTourCompleted]);

  useEffect(() => {
    const handleToggle = (e) => {
      const isModalOpen = e.detail;
      if (!isModalOpen && !isTourCompleted && !getTourState().dismissed) {
        setIsTooltipVisible(true);
        setIsPaused(false);
      }
    };

    window.addEventListener("pageInfoModalToggle", handleToggle);
    return () => {
      window.removeEventListener("pageInfoModalToggle", handleToggle);
    };
  }, [isTourCompleted]);

  // Sync state to unified storage
  useEffect(() => {
    saveTourState({
      step: tourStep,
      paused: isPaused,
      visible: isTooltipVisible,
      completed: isTourCompleted,
    });
  }, [tourStep, isPaused, isTooltipVisible, isTourCompleted]);

  // Wait for the target; missing controls never count as completed actions.
  useEffect(() => {
    if (isTooltipVisible && steps[tourStep] && !isTourCompleted && !isPaused && !isWaitingForCompletion) {
      setIsTargetReady(false);
      const targetSelector = steps[tourStep].target;
      let checkAttempts = 0;

      const checkInterval = setInterval(() => {
        const element = document.querySelector(targetSelector);
        if (element) {
          // Target exists, clear the check and show tooltip
          setIsTargetReady(true);
          clearInterval(checkInterval);
        } else {
          checkAttempts++;
          // Pause without completing anything if the target stays unavailable.
          if (checkAttempts >= 300) {
            clearInterval(checkInterval);
            handleTourDismiss();
          }
        }
      }, 100);

      // Do an immediate check to prevent flicker delay if element already exists
      if (document.querySelector(targetSelector)) {
        setIsTargetReady(true);
        clearInterval(checkInterval);
      }

      return () => clearInterval(checkInterval);
    } else {
      setIsTargetReady(false);
    }
  }, [tourStep, isTooltipVisible, isTourCompleted, isPaused, isWaitingForCompletion]);

  // skipped === true: the user chose Skip, so do not continue into the matrix walk-through
  const handleTourComplete = (skipped) => {
    if (skipped === true) {
      markGuideDone("cargo");
      return handleTourDismiss();
    }
    setIsTooltipVisible(false);
    setIsPaused(false);
    setIsTourCompleted(true);
    markGuideDone("cargo");
    if (!isGuideDone("matrix")) matrixTour.start(false);
  };

  const handleTourDismiss = () => {
    setIsTooltipVisible(false);
    setIsPaused(true);
    saveTourState({ paused: true, dismissed: true });
    setIsWaitingForCompletion(false);
  };

  const nextTourStep = () => {
    setIsWaitingForCompletion(false);
    // Saving must succeed before creation is complete.
    const currentStep = steps[tourStep];
    if (currentStep?.actionRequired) {
      if (!formErrors.length) document.querySelector(currentStep.target)?.click();
      else handleTourDismiss();
      return;
    }
    if (currentStep && currentStep.onNextAction) {
      currentStep.onNextAction();
    }

    if (tourStep < steps.length - 1) {
      setTourStep((prev) => prev + 1);
      setIsTooltipVisible(true);
      setIsPaused(false);
    } else {
      handleTourComplete();
    }
  };

  const lastAdvancedStepRef = useRef(-1);

  const handleTourAction = (key) => {
    if (isTourCompleted || getTourState().dismissed) return;
    // Prevent double-advancement for the same step index
    if (lastAdvancedStepRef.current === tourStep) return;

    if (steps[tourStep]?.key === key) {
      setIsTooltipVisible(false);
      setIsWaitingForCompletion(true);
      setIsPaused(false);
    }
  };

  const handleActionComplete = (key) => {
    if (key === "created") {
      saveGuideProgress("cargo", { created: true });
      if (!isGuideDone("matrix")) {
        saveGuideProgress("matrix", { index: 0, ctx: {}, paused: false });
        setValue(0);
        handleTourComplete();
      }
      return;
    }
    if (key === "matrix" && !isGuideDone("matrix")) {
      matrixTour.complete();
      return;
    }
    if (isTourCompleted || getTourState().dismissed) return;
    // If it's the last step and correct key, finish tour immediately
    if (tourStep === steps.length - 1 && steps[tourStep]?.key === key) {
      handleTourComplete();
      return;
    }

    if ((isWaitingForCompletion || isPaused) && steps[tourStep]?.key === key) {
      lastAdvancedStepRef.current = tourStep;
      nextTourStep();
    }
  };

  const handleActionError = (key) => {
    if (isTourCompleted) return;
    if (isWaitingForCompletion && steps[tourStep]?.key === key) {
      setIsWaitingForCompletion(false);
      setIsTooltipVisible(true); // Show tooltip again on error so they can retry
    }
  };

  const startTour = () => {
    setTourStep(0);
    setIsTooltipVisible(true);
    setIsPaused(false);
    setValue(0);
    localStorage.removeItem("orders_tour_completed");
  };

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [searchValue, setSearchValue] = useState("");
  const onSearchCargo = (search) => {
    dispatch(actions.clientQueryUser({ search: search }))
      .catch((err) => {
        toastError(err?.data?.message || "Something went wrong");
      });
    if (organizationId) dispatch(actions.clientQueryOrg({ search: search }))
  };
  const delaySearch = useCallback(
    _.debounce((data) => onSearchCargo(data), 500),
    []
  );
  const onSearchChange = (e) => {
    setSearchValue(e.target.value)
    delaySearch(e.target.value);
  }

  const addQueryModalRef = useRef();
  useEffect(() => {
    setMyFilters((prev) => ({ ...prev, ...allClientOrdersPagination }));
  }, [allClientOrdersPagination]);
  useEffect(() => {
    setAllFilters((prev) => ({ ...prev, ...allOrganizationOrdersPagination }));
  }, [allOrganizationOrdersPagination]);
  useEffect(() => {
    if (!hasFetched && (!allClientOrders || !allClientOrders.length)) {
      setIsInitialLoading(true);
      setLoader(true);
    }
    Promise.all([
      !!organizationId ? dispatch(actions.clientQueryOrg()) : null,
      dispatch(actions.clientQueryUser()),
      dispatch(actions.getQueryFormatForUser()),
    ])
      .then(() => {
        setIsInitialLoading(false);
        setLoader(false);
        setHasFetched(true);
      })
      .catch((err) => {
        setIsInitialLoading(false);
        setLoader(false);
        setHasFetched(true);
        toastError(err?.data?.message || "Something went wrong.");
      });
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setSearchType({ ...initialSearchType });
  };

  const onCloseModal = () => {
    setOpen(false);
    setIsEdit(false);
    setIsStepper(false)
    setIsDetailView(false);
    handleActionComplete("view");
    setSelectedQuery({});
    setSelectedPorts([]);
    setSelectedPortsCommence([]);
    setCargoAnalysisStepper(false)
  };

  const onClickEditQuery = (query) => {
    calculatorActions
      .cargoByQuery(query._id)
      .then((res) => {
        setSelectedPorts(
          res.data.cargoList.filter((item) => item.operation !== "Commence")
        );
        setSelectedPortsCommence(
          res.data.cargoList
            .filter((item) => item.operation === "Commence")
        );
        setSelectedQuery(res.data);
        setOpen(true);
        setIsEdit(true);
      })
      .catch((err) => {
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onCargoAnalysisClick = (id, clientQuery) => {
    calculatorActions
      .getGeneralMasterData("cargo-analysis-vessel-list")
      .then((res) => {
        setCargoAnalysisStepper(true)
        setAnalysisVessel(res?.data?.list)
        setSelectedPorts(
          clientQuery?.cargoList?.filter((item) => item.operation !== "Commence")
        );
        setCargoAnalysisData({ clientQuery })
      })
      .catch((err) => {
        toastError(err?.data?.message || "Something went wrong");
      });
  };
  const onStepperQuery = (queryid) => {
    calculatorActions
      .cargoByQuery(queryid)
      .then((res) => {
        setSelectedPorts(
          res.data.cargoList.filter((item) => item.operation !== "Commence")
        );
        setSelectedPortsCommence(
          res.data.cargoList
            .filter((item) => item.operation === "Commence")
        );
        setSelectedQuery(res.data);
        // setOpen(true);
        setIsStepper(true)

      })
      .catch((err) => {
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onChangeSwitchQuery = (queryDetails) => {
    const queryId = queryDetails._id;
    const isFindIndex = queryOrderList.findIndex((item) => item === queryId);
    let updatedQueryList = [...queryOrderList];
    if (isFindIndex > -1) {
      updatedQueryList.splice(isFindIndex, 1);
    } else {
      updatedQueryList.push(queryId);
    }
    if (updatedQueryList.length === 0) updatedQueryList = [null];
    const queryAccess = queryDetails.isShowInCalculator ? false : true;
    calculatorActions
      .updateChangeSwitchQuery({
        id: queryId,
        isShowInCalculator: queryAccess,
      })
      .then(async (res) => {
        toastSuccess(`Query access updated to ${queryAccess}.`);
        if (queryAccess) handleActionComplete("matrix");
        Promise.all([
          !!organizationId ? dispatch(actions.clientQueryOrg()) : null,
          dispatch(actions.clientQueryUser()),
          dispatch(actions.getQueryFormatForUser()),
        ]).catch((err) => {
          toastError(err?.data?.message || "Something went wrong.");
        });
      })
      .catch((err) => {
        handleActionError("matrix");
        toastError(err?.data?.message);
      });
  };

  const onShareOrgSaveClick = () => {
    if (!shareLabel) return
    onClickShareWithTeam()
    setShareConfirm(false)
    setShareLabel("")
  }
  const onShareOrgClick = () => {
    setShareConfirm(!isShareConfirm)
  }
  const onChangePinQuery = (queryDetails) => {
    const queryId = queryDetails._id;
    const isFindIndex = queryOrderList.findIndex((item) => item === queryId);
    let updatedQueryList = [...queryOrderList];
    if (isFindIndex > -1) {
      updatedQueryList.splice(isFindIndex, 1);
    } else {
      updatedQueryList.push(queryId);
    }
    if (updatedQueryList.length === 0) updatedQueryList = [null];
    const queryAccess = queryDetails.access === "global" ? "private" : "global";
    calculatorActions
      .updateClientQueryAccess({
        id: queryId,
        access: queryAccess,
      })
      .then(async (res) => {
        toastSuccess(`Query access updated to ${queryAccess}.`);
        Promise.all([
          !!organizationId ? dispatch(actions.clientQueryOrg()) : null,
          dispatch(actions.clientQueryUser()),
          dispatch(actions.getQueryFormatForUser()),
        ]).catch((err) => {
          toastError(err?.data?.message || "Something went wrong.");
        });
      })
      .catch((err) => {
        toastError(err?.data?.message);
      });
  };

  const getAllFleets = async (fromCheck = false, fromPagination) => {
    await calculatorActions.userFleets(fromPagination ? fromPagination : vesselFilters)
      .then((res) => {
        setHasFetchedFleets(true);
        setFleets(
          (res.data && Array.isArray(res.data.list) && res.data.list) || []
        );
        setVesselFilters({
          totalPages: res?.data?.totalItems,
          offset: res?.data?.currentPage,
          pageCount: res?.data?.totalPages,
          limit: res?.data?.perPage
        })
      })
      .catch((err) => {
        setHasFetchedFleets(true);
        toastError();
      });
  };

  const onClickSaveCargoVesselLabel = async () => {
    const estimateName = saveLabelCargoVessel.trim();
    if (!estimateName) {
      setSaveLabelCargoVesselError(true);
      return;
    }
    const calculatedData = {
      name: estimateName,
      vessel: selectedVessel,
      clientQuery: selectedCargoVessel,
      fleet: selectedVessel,
      calculatedFleetResult: selectedVessel,
    };
    const resultLink = await calculatorActions.saveCalculatedCostResult(calculatedData);
    onCloseSaveCargo();
    onCloseCargoVessel()
    const newTab = window.open(`/saved/${resultLink?.data?._id}?code=cargo`, '_blank');
    if (newTab) newTab.opener = null;
  }




  const onClickSaveCargoLabel = async () => {

    if (!!saveLabel) {
      const cargolistdetails = []
      selectedCargo?.cargoList?.forEach(element => {
        if (element?.operation?.toLowerCase() === "loading" || element?.operation?.toLowerCase() === "discharging") {
          cargolistdetails.push({
            label: element?.label,
            despatch: element?.despatch,
            voyType: element?.voyType,
            account: element?.account,
            firstLoadPort: element?.firstLoadPort,
            lastDischargePort: element?.lastDischargePort,
            cargo: element?.cargo,
            freight: element?.freight,
            profit: element?.profit,
            duration: element?.duration,
            freightMode: element?.freightMode,
            laycanBegin: element?.laycanBegin,
            laycanEnd: element?.laycanEnd,
            broker: element?.broker,
            lumpsumFreight: element?.lumpsumFreight,
            stowFactor: element?.stowFactor,
            multiParcelMode: element?.multiParcelMode,
            operation: element?.operation,
            port: element?.port,
            cargoQty: element?.cargoQty,
            tolerance: element?.tolerance,
            rate: element?.rate,
            terms: element?.terms,
            turnTime: element?.turnTime,
            pdaRemarks: element?.pdaRemarks,
            grade: element?.grade,
            adcom: element?.adcom,
            brokerage: element?.brokerage,
            ttlComm: element?.ttlComm,
            netFrt: element?.netFrt,
            factor: element?.factor,
            other: element?.other,
            index: element?.index,
            country: element?.country,
            portId: element?.portId,
            notes: element?.notes,
            demurrageRate: element?.demurrageRate || 0,
            despatchRate: element?.despatchRate || 0,
          })
        }
      });
      const vesselVoyageData = []
      selectedCargo?.cargoList?.forEach(element => {
        if (element?.operation?.toLowerCase() === "loading" || element?.operation?.toLowerCase() === "discharging") {
          vesselVoyageData.push({
            operation: element?.operation,
            port: element?.port,
            lat: element?.lat,
            lng: element?.lng,
            cargoQty: element?.cargoQty,
            cargo: element?.cargo,
            min: element?.min,
            max: element?.max,
            laycanStartDate: null,
            laycanEndDate: null,
            stowFactor: element?.stowFactor,
            actualCargoQty: 0,
            tolerance: element?.tolerance || 0,
            arrivalTime: null,
            departureTime: null,
            portStay: 0
          })
        }
      });

      const customerCargo = {
        ...selectedCargo,
        cargoList: cargolistdetails,
      }
      const cargocargo = {
        customerCargo: customerCargo,
        vendorCargo: customerCargo,
        vesselVoyageData: vesselVoyageData,
        queryId: selectedCargo?._id,
        cargoCargoName: saveLabel,
        organizationId: selectedCargo?.organizationId,
        version: selectedCargo?.version,
        system: selectedCargo?.system,
        name: selectedCargo?.name,
        miscExp: selectedCargo?.miscExp,
        miscRevenue: selectedCargo?.miscRevenue,
        laycanStartDate: selectedCargo?.laycanStartDate,
        laycanEndDate: selectedCargo?.laycanEndDate,
        remarkMessage: selectedCargo?.remarkMessage,
        qtyMode: selectedCargo?.qtyMode,
        freightMode: selectedCargo?.freightMode,
        access: selectedCargo?.access,
        firstLoadPort: selectedCargo?.firstLoadPort,
        lastDischargePort: selectedCargo?.lastDischargePort,
        cargo: selectedCargo?.cargo,
        loadQty: selectedCargo?.loadQty,
        createdUser: selectedCargo?.createdUser,
        label: selectedCargo?.label,
        voyType: selectedCargo?.voyType,
        account: selectedCargo?.account,
      }
      setLoader(true);
      actions.addCargoCargoDetails(cargocargo)
        .then(() => {
          setLoader(false);
          toastSuccess("Cargo cargo added in estimates successfully");
          onCloseSaveCargo();
        })
        .catch((err) => {
          setLoader(false);
          onCloseSaveCargo();
          toastError(err?.data?.message || "Something went wrong");
        });

    } else {
      toastError("Please enter cargo label")
    }
  };

  const onCloseSaveCargo = () => {
    setSaveModal(false);
    setSelectedCargo(null);
    setSaveLabel("");
  };
  const onClickSaveCargo = (cargo) => {
    setSelectedCargo(cargo);
    setSaveLabel(saveLabel);
    setSaveModal(true);
  };


  const onChangeVesselPagination = (type = "") => {
    let newVesselFilters = {};
    if (type === "inc") {
      newVesselFilters = { ...vesselFilters, offset: vesselFilters.offset + 1 };
    } else if (type === "first") {
      newVesselFilters = { ...vesselFilters, offset: 1 };
    } else {
      if (vesselFilters.offset === 1) return;
      newVesselFilters = { ...vesselFilters, offset: vesselFilters.offset - 1 };
    }
    setLoader(true);
    getAllFleets(false, newVesselFilters)

  };
  const onCreateVesselClick = () => {
    if (!selectedVessel?._id) {
      return toastError("Please select one vessel")
    }
    setSaveLabelCargoVesselError(false);
    setSaveModalCargoVessel(true)
  }
  const onClickVessel = (e, item) => {
    if (e?.target?.checked) {
      setSelectedVessel(item)
    } else {
      setSelectedVessel({})
    }
  }
  const onCloseSaveCargoCargoVessel = () => {

    setSelectedCargoVessel(null);
    setSaveLabelCargoVessel("");
    // setSaveModalCargoVessel(false);
    setModelVessel(false)
    setSelectedVessel({})

  };
  const onCloseCargoVessel = () => {

    setSaveModalCargoVessel(false);
    setSaveLabelCargoVesselError(false);

  };
  const onClickSaveCargoVessel = (id, cargo) => {
    getAllFleets()
    setSelectedCargoVessel(cargo);
    setSaveLabelCargoVessel(saveLabelCargoVessel);
    // setSaveModalCargoVessel(true);
    setModelVessel(true);


  };

  const onDeleteQuery = () => {
    setLoader(true);
    Promise.all([actions.querydelete(deleteId._id)])
      .then(async () => {
        toastSuccess("Query deleted successfully");
        setDeleteModal(false);
        await dispatch(actions.clientQueryUser());
        setLoader(false);
      })
      .catch((err) => {
        setDeleteModal(false);
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onChangeSearchType = (e, key = "") => {
    let { name = "", value = "" } = e.target || {};
    if (!value) name = "";
    let updatedSearchType = { ...searchType };
    if (name === "loadQty") {
      let valueSplit = updatedSearchType[name].split(",");
      let startValue = valueSplit[0] || "";
      let endValue = valueSplit[1] || "";
      if (key === "start") startValue = value;
      else endValue = value;
      value = `${startValue},${endValue}`;
    } else if (name === "createdDate") {
      let valueSplit = updatedSearchType[name].split(",");
      let startValue = valueSplit[0] || "";
      let endValue = valueSplit[1] || "";
      if (key === "start") startValue = moment(value).valueOf();
      else endValue = moment(value).valueOf();
      value = `${startValue},${endValue}`;
    }
    setSearchType({ ...initialSearchType, type: name, [name]: value });
  };

  const onClickSubmitSearch = async () => {
    setLoader(true);
    if (searchType.type) {
      let searchRequest = actions.allQueryByCharForUser;
      if (value !== 1) searchRequest = actions.allQueryByCharForOrg;
      await dispatch(
        searchRequest({
          searchParam: searchType[searchType.type],
          type: searchType.type,
          offset: 10,
        })
      );
    } else {
      let searchRequest = actions.clientQueryUser;
      if (!organizationId) return null;
      if (value !== 1) searchRequest = actions.clientQueryOrg;
      await dispatch(searchRequest());
    }
    setLoader(false);
  };

  const onSubmitConfirm = () => {
    handleTourAction("save");
    if (isEdit) {
      setWarningPopup(true)
    } else {
      addQueryModalRef.current.onSubmitQuery()
    }
  };

  const onExportCargoPdf = async (item, mode) => {
    setLoader(true);
    try {
      await exportCargoPdf(item, mode, prepareShare);
    } catch (err) {
      toastError(err?.data?.message || "Could not create the PDF");
    }
    setLoader(false);
  };

  const onClickCreateCopy = () => {
    setWarningPopupForCopy(true)
  };

  const onClickShareWithTeam = () => {
    addQueryModalRef.current.onSubmitQuery(true, shareLabel)
  }

  const onChangeMyPagination = (type = "") => {
    let newMyFilters = {};
    if (type === "inc") {
      newMyFilters = { ...myFilters, offset: myFilters.offset + 1 };
    } else if (type === "first") {
      newMyFilters = { ...myFilters, offset: 1 };
    } else {
      if (myFilters.offset === 1) return;
      newMyFilters = { ...myFilters, offset: myFilters.offset - 1 };
    }
    setLoader(true);
    dispatch(actions.clientQueryUser(newMyFilters))
      .then(() => {
        setLoader(false);
        setMyFilters(newMyFilters);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onChangeMySort = (sortBy, sortDirection) => {
    let newMyFilters = { ...myFilters, sortBy, sortDirection, offset: 1 };

    let apiFilters = { ...newMyFilters };
    if (!apiFilters.sortBy) {
      apiFilters.sortBy = "createdAt";
      apiFilters.sortDirection = "desc";
    }

    setLoader(true);
    dispatch(actions.clientQueryUser(apiFilters))
      .then(() => {
        setLoader(false);
        setMyFilters(newMyFilters);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onChangeAllPagination = (type = "") => {
    let newallFilters = {};
    if (type === "inc") {
      newallFilters = { ...allFilters, offset: allFilters.offset + 1 };
    } else if (type === "first") {
      newallFilters = { ...allFilters, offset: 1 };
    } else {
      if (allFilters.offset === 1) return;
      newallFilters = { ...allFilters, offset: allFilters.offset - 1 };
    }
    setLoader(true);
    if (!organizationId) return null;
    dispatch(actions.clientQueryOrg(newallFilters))
      .then(() => {
        setLoader(false);
        setAllFilters(newallFilters);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onChangeAllSort = (sortBy, sortDirection) => {
    let newallFilters = { ...allFilters, sortBy, sortDirection, offset: 1 };

    let apiFilters = { ...newallFilters };
    if (!apiFilters.sortBy) {
      apiFilters.sortBy = "createdAt";
      apiFilters.sortDirection = "desc";
    }

    setLoader(true);
    if (!organizationId) return null;
    dispatch(actions.clientQueryOrg(apiFilters))
      .then(() => {
        setLoader(false);
        setAllFilters(newallFilters);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onClickClearMyOrders = () => {
    setLoader(true);
    dispatch(actions.clientQueryUser())
      .then(() => {
        setSearchType({ ...initialSearchType });
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onClickClearAllOrders = () => {
    if (!organizationId) return null;
    setLoader(true);
    dispatch(actions.clientQueryOrg())
      .then(() => {
        setSearchType({ ...initialSearchType });
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
  };

  const onOpenDeleteModal = (query) => {
    setDeleteId(query);
    setDeleteModal(true);
  };
  const handleChangeIndex = (index) => {
    setValue(index);
  };

  const handleDateRangeChange = async (newDateRange) => {
    const { startDate, endDate } = newDateRange;

    // Ensure dates are in ISO string format
    const formattedStartDate = startDate?.toISOString() || '';
    const formattedEndDate = endDate?.toISOString() || '';
    if (value === 1) {
      dispatch(actions.clientQueryOrg({
        organizationId,
        startDate: formattedStartDate,
        endDate: formattedEndDate
      }))
    } else {
      dispatch(actions.clientQueryUser({
        startDate: formattedStartDate,
        endDate: formattedEndDate
      }))
    }
  };

  const handleOpenCargoContractModel = (data) => {
    setModelCargoContract(true)
    setFormData({
      ...formData,
      cargoList: data?.cargoList,
    })
    setSelectedCargoForContract(data)
  }

  const handleCloseCargoContractModel = () => {
    setModelCargoContract(false)
    setFormData({
      ...formData,
      counterPartyNames: [],
      brokers: [],
      myAddressBook: [],
      organizationAddress: [],
    })
  }

  const handleContractInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTextContractUpload = (e) => {
    if (e.target.files[0]) {
      setTextContract(e.target.files[0]);
    }
  };

  const handleAttachmentUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(file => file.size <= 5 * 1024 * 1024);

      if (validFiles.length !== newFiles.length) {
        alert('Some files exceed the 5 MB size limit and were not added.');
      }

      setAttachments([...attachments, ...validFiles]);
    }
  };

  const removeAttachment = (index) => {
    const updatedAttachments = [...attachments];
    updatedAttachments.splice(index, 1);
    setAttachments(updatedAttachments);
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.contractName) {
      toastError('Contract Name is Required');
      return;
    }

    const formPayload = new FormData();

    if (textContract) {
      const fileType = textContract.type || 'application/octet-stream';
      const fileName = textContract.name || 'contract';

      const textBlob = new Blob([textContract], { type: fileType });
      const textFile = new File([textBlob], fileName, { type: fileType });

      formPayload.append('files', textFile);
      formPayload.append('textContractMetadata', JSON.stringify({ fileType: 'textContract' }));
    }

    if (attachments?.length) {
      attachments.forEach(file => formPayload.append('files', file));
    }

    const { cargoList, ...rest } = formData;

    let finalCargoDetails = {};


    const cargolistdetails = [];
    selectedCargoForContract?.cargoList?.forEach(element => {
      const op = element?.operation?.toLowerCase();
      if (op === "loading" || op === "discharging") {
        cargolistdetails.push({
          label: element?.label,
          despatch: element?.despatch,
          voyType: element?.voyType,
          account: element?.account,
          firstLoadPort: element?.firstLoadPort,
          lastDischargePort: element?.lastDischargePort,
          cargo: element?.cargo,
          freight: element?.freight,
          profit: element?.profit,
          duration: element?.duration,
          freightMode: element?.freightMode,
          laycanBegin: element?.laycanBegin,
          laycanEnd: element?.laycanEnd,
          broker: element?.broker,
          lumpsumFreight: element?.lumpsumFreight,
          stowFactor: element?.stowFactor,
          multiParcelMode: element?.multiParcelMode,
          operation: element?.operation,
          port: element?.port,
          cargoQty: element?.cargoQty,
          tolerance: element?.tolerance,
          rate: element?.rate,
          terms: element?.terms,
          turnTime: element?.turnTime,
          pdaRemarks: element?.pdaRemarks,
          grade: element?.grade,
          adcom: element?.adcom,
          brokerage: element?.brokerage,
          ttlComm: element?.ttlComm,
          netFrt: element?.netFrt,
          factor: element?.factor,
          other: element?.other,
          index: element?.index,
          country: element?.country,
          portId: element?.portId,
          notes: element?.notes,
          demurrageRate: element?.demurrageRate || 0,
          despatchRate: element?.despatchRate || 0,
        });
      }
    });

    const vesselVoyageData = [];
    selectedCargoForContract?.cargoList?.forEach(element => {
      const op = element?.operation?.toLowerCase();
      if (op === "loading" || op === "discharging") {
        vesselVoyageData.push({
          _id: uuidv4(),
          operation: element?.operation,
          port: element?.port,
          lat: element?.lat,
          lng: element?.lng,
          cargoQty: element?.cargoQty,
          cargo: element?.cargo,
          min: element?.min,
          max: element?.max,
          laycanStartDate: null,
          laycanEndDate: null,
          stowFactor: element?.stowFactor,
          actualCargoQty: 0,
          tolerance: element?.tolerance || 0,
          arrivalTime: null,
          departureTime: null,
          portStay: 0
        });
      }
    });

    const customerCargo = {
      ...selectedCargoForContract,
      cargoList,
    };

    finalCargoDetails = {
      customerCargo: customerCargo,
      vesselVoyageData: vesselVoyageData,
      queryId: selectedCargoForContract?._id,
      cargoCargoName: saveLabel,
      organizationId: selectedCargoForContract?.organizationId,
      version: selectedCargoForContract?.version,
      system: selectedCargoForContract?.system,
      name: selectedCargoForContract?.name,
      miscExp: selectedCargoForContract?.miscExp,
      miscRevenue: selectedCargoForContract?.miscRevenue,
      laycanStartDate: selectedCargoForContract?.laycanStartDate,
      laycanEndDate: selectedCargoForContract?.laycanEndDate,
      remarkMessage: selectedCargoForContract?.remarkMessage,
      qtyMode: selectedCargoForContract?.qtyMode,
      freightMode: selectedCargoForContract?.freightMode,
      access: selectedCargoForContract?.access,
      firstLoadPort: selectedCargoForContract?.firstLoadPort,
      lastDischargePort: selectedCargoForContract?.lastDischargePort,
      cargo: selectedCargoForContract?.cargo,
      loadQty: selectedCargoForContract?.loadQty,
      createdUser: selectedCargoForContract?.createdUser,
      label: selectedCargoForContract?.label,
      voyType: selectedCargoForContract?.voyType,
      account: selectedCargoForContract?.account,
    };


    // Push the final payload
    formPayload.append('data', JSON.stringify({
      ...rest,
      cargoDetails: finalCargoDetails,
      organizationId,
      cargoId: selectedCargoForContract._id,
    }));

    // Submit and handle response
    actions.createCargoContract(formPayload)
      .then((res) => {
        setModelCargoContract(false);
        setFormData(initialContractFields);
        setAttachments([]);
        setTextContract(null);
        toastSuccess('Cargo Contract created successfully!');
      })
      .catch((error) => {
        // Added error handling
        toastError(error?.data?.message);
      });
  };

  const handleRestart = () => {
    const resumeStep = isGuideDone("cargo") ? 0 : getTourState().step || 0;
    setTourStep(resumeStep);
    if (resumeStep > 0) setOpen(true);
    setIsTooltipVisible(true);
    setIsPaused(false);
    setIsWaitingForCompletion(false);
    setIsTourCompleted(false);
    saveTourState({ step: resumeStep, completed: false, paused: false, dismissed: false });
  };

  const [showVesselCard, setShowVesselCard] = useState(false);
  const [allInMatrix, setAllInMatrix] = useState(false);
  const matrixTour = useGuidedTour({
    guideKey: "matrix",
    steps: MATRIX_STEPS,
    busy: Boolean(loader || isInitialLoading),
    onFinish: () => {
      setAllInMatrix(false);
      setShowVesselCard(true);
    },

  });

  const resumedCargo = useRef(false);
  useEffect(() => {
    if (isInitialLoading || loader || resumedCargo.current || !allClientOrders.length) return;
    resumedCargo.current = true;
    if (!isGuideDone("matrix")) {
      setIsTooltipVisible(false);
      setIsTourCompleted(true);
      markGuideDone("cargo");
      const order = allClientOrders.find((item) => !item.isShowInCalculator) || allClientOrders[0];
      saveGuideProgress("matrix", { index: order.isAllStepClear ? 4 : 0, ctx: { orderId: order._id } });
      if (order.isAllStepClear && order.isShowInCalculator) matrixTour.complete();
      else matrixTour.start(false);
    }
  }, [isInitialLoading, loader, allClientOrders]);

  useEffect(() => {
    const complete = () => handleActionComplete("matrix");
    window.addEventListener("guide:cargo-matrix", complete);
    return () => window.removeEventListener("guide:cargo-matrix", complete);
  });

  const [matrixReplay, setMatrixReplay] = useState(false);
  useEffect(() => {
    if (!matrixReplay || isInitialLoading || loader) return;
    setMatrixReplay(false);
    const needsSteps = allClientOrders.find((item) => !item.isAllStepClear);
    const notShown = allClientOrders.find((item) => item.isAllStepClear && !item.isShowInCalculator);
    if (needsSteps) {
      saveGuideProgress("matrix", { index: 0, ctx: {} });
      matrixTour.start(true);
    } else if (notShown) {
      saveGuideProgress("matrix", { index: MATRIX_STEPS.length - 1, ctx: { orderId: notShown._id } });
      matrixTour.start(false);
    } else if (allClientOrders.length) {
      setAllInMatrix(true);
      setShowVesselCard(true);
    } else {
      handleRestart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixReplay, isInitialLoading, loader, allClientOrders]);

  // Guide Me menu / "Next step" card ask for a guide by key; start the ones owned by this page.
  useEffect(() => {
    const run = () => {
      if (takeGuideRequest("cargo")) handleRestart();
      if (takeGuideRequest("matrix")) {
        setValue(0);
        setMatrixReplay(true);
      }
    };
    run();
    window.addEventListener("guide:request", run);
    return () => window.removeEventListener("guide:request", run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    {
      key: 0,
      label: "my Orders",
      tooltip: "displays orders entries created by you.",
    },
    {
      key: 1,
      label: "all Orders",
      tooltip:
        "displays orders shared with your organization. Copy any order to your personal list.",
    },
  ];
  return (
    <div className="flex flex-col min-h-full">
      {shareDialog}
      <div className="flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
        <CustomTabs tabs={tabs} active={value} setActive={setValue} />

        <div className="flex-1 min-w-[160px] max-w-[400px] mx-1">
          <SearchBar onDateRangeChange={handleDateRangeChange} placeholder={"Search with Name..."} setSearchValue={onSearchChange} searchValue={searchValue} />
        </div>

        <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
          <GuideMenu />
          <button
            type="button"
            className="flex items-center gap-1.5 px-2.5 py-1 h-[26px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none shadow-2xs cursor-pointer hover:bg-[#0c2f50] transition-colors whitespace-nowrap"
            data-spotlight="create-ai-btn"
            onClick={() => setCargoAiOpen(true)}
          >
            <LuSparkles size={14} aria-hidden="true" /> Create with AI
          </button>
          <button type="button" className="flex items-center gap-1.5 px-2.5 py-1 h-[26px] text-[11px] font-medium text-slate-900 bg-white hover:bg-slate-50 border border-slate-300 rounded-none shadow-2xs transition-colors cursor-pointer whitespace-nowrap" onClick={() => {
            setOpen(true);
            handleTourAction("create");
          }} data-spotlight="add-cargo-btn"><BsPlusLg size={10} className="text-[#313C4C]" aria-hidden="true" /> New Order</button>
        </div>
      </div>

      <div className="w-full">
        {
          value === 0 && (
            <Box pt={0.25}>
              <MyOrdersTable
                loader={loader}
                isInitialLoading={isInitialLoading}
                hasFetched={hasFetched}
                allClientOrders={allClientOrders}
                queryOrderList={queryOrderList}
                onClickEditQuery={onClickEditQuery}
                onChangePinQuery={onChangeSwitchQuery}
                onDeleteQuery={onOpenDeleteModal}
                searchType={searchType}
                onChangeSearchType={onChangeSearchType}
                onClickSubmitSearch={onClickSubmitSearch}
                filters={myFilters}
                onChangePagination={onChangeMyPagination}
                onChangeSort={onChangeMySort}
                onClickClear={onClickClearMyOrders}
                onStepperQuery={onStepperQuery}
                onClickSaveCargo={onClickSaveCargo}
                onClickSaveCargoVessel={onClickSaveCargoVessel}
                onCargoAnalysisClick={onCargoAnalysisClick}
                handleOpenCargoContractModel={handleOpenCargoContractModel}
                onExportPdf={onExportCargoPdf}
                handleTourAction={handleTourAction}
                handleActionComplete={handleActionComplete}
                handleActionError={handleActionError}
              />
            </Box>
          )
        }
        {
          value === 1 && (
            <Box pt={0.25}>
              {organizationId ? <AllOrdersTable
                allOrganizationOrders={allOrganizationOrders}
                isInitialLoading={isInitialLoading}
                onClickEditQuery={onClickEditQuery}
                setIsDetailView={setIsDetailView}
                searchType={searchType}
                onChangeSearchType={onChangeSearchType}
                onClickSubmitSearch={onClickSubmitSearch}
                filters={allFilters}
                onChangePagination={onChangeAllPagination}
                onChangeSort={onChangeAllSort}
                onClickClear={onClickClearAllOrders}
                hasFetched={hasFetched}
              /> :
                <>
                  You are not part of any organization.
                </>
              }
            </Box>
          )
        }
      </div>
      <CargoAIModal
        open={cargoAiOpen}
        handleClose={() => setCargoAiOpen(false)}
        setQuery={setSelectedQuery}
        setPortOperations={setSelectedPorts}
        onFilled={() => {
          setCargoAiOpen(false);
          setIsEdit(false);
          setIsDetailView(false);
          setOpen(true);
        }}
      />
      <CustomAddModal
        open={open}
        title={isEdit ? "Update Cargo" : "New Cargo"}
        maxWidth="lg"
        saveLabel={isEdit ? "Update" : "Create"}
        addThirdBtn={isEdit}
        thirdBtnAction={onClickCreateCopy}
        thirdBtnLabel="Create New Order"
        buttonCase="capitalize"
        handleClose={onCloseModal}
        isDetailView={isDetailView}
        onSubmitConfirm={onSubmitConfirm}
        isDowloadPdf
      >
        <AddNewOrder
          hideCreateAI
          isOpen={open}
          ref={addQueryModalRef}
          setLoader={setLoader}
          loader={loader}
          onCloseModal={onCloseModal}
          selectedQuery={selectedQuery}
          selectedPorts={selectedPorts}
          isEdit={isEdit}
          selectedPortsCommence={selectedPortsCommence}
          onChangePinQuery={onChangePinQuery}
          isDetailView={isDetailView}
          setIsEdit={setIsEdit}
          onShareOrgClick={onShareOrgClick}
          tabvalue={value}
          tourStep={tourStep}
          nextTourStep={nextTourStep}
          handleTourAction={handleTourAction}
          handleActionComplete={handleActionComplete}
          handleActionError={handleActionError}
        />
      </CustomAddModal>
      <CustomShowModal
        open={isStepper}
        title="Add to Matrix"
        maxWidth="lg"
        saveLabel={"Update"}
        handleClose={onCloseModal}
        isDetailView={isDetailView}
        isDowloadPdf
      >
        <CreateNewCargo
          ref={addQueryModalRef}
          setLoader={setLoader}
          onCloseModal={onCloseModal}
          selectedQuery={selectedQuery}
          selectedPorts={selectedPorts}
          selectedPortsCommence={selectedPortsCommence}
          isEdit={isStepper}
          onChangePinQuery={onChangePinQuery}
          isDetailView={isDetailView}
          setIsEdit={setIsStepper}
        />
      </CustomShowModal>

      <CustomConfirmModal
        open={deleteModal}
        handleClose={() => setDeleteModal(false)}
        title="Delete Order"
        message="Are you sure you want to delete this order?"
        note="This action cannot be undone."
        onSubmitConfirm={onDeleteQuery}
        type="warning"
        confirmText="DELETE"
        cancelText="Cancel"
      />

      <CustomAddModal
        open={isShareConfirm}
        handleClose={onShareOrgClick}
        onSubmitConfirm={onShareOrgSaveClick}
        maxWidth="sm"
        saveLabel="Share Cargo"
        title="Share Cargo with Team"
      >
        <div className="flex flex-col gap-6">

          {/* 1. Feature Highlight / Info Box 
        Using Indigo colors for "Team/Organization" context
    */}
          <div className="relative overflow-hidden rounded-lg border border-[#C7D2FE] bg-[#EEF2FF] p-4">
            <div className="flex items-start gap-3">
              {/* Icon: Users / Group */}
              <div className="shrink-0 mt-0.5">
                <svg
                  className="h-5 w-5 text-[#4F46E5]"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 5.472m0 0a9.09 9.09 0 00-3.261 3.223 3 3 0 004.286 4.044M16.5 7.5a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM12 3a3 3 0 013 3" />
                </svg>
              </div>

              {/* Text Content */}
              <div>
                <h3 className="text-sm font-semibold text-[#312E81]">
                  Organization-Wide Access
                </h3>
                <p className="mt-1 text-sm text-[#4338CA] leading-relaxed">
                  This cargo will be listed in the <strong>All Orders</strong> tab and visible to all organization members.
                </p>
              </div>
            </div>
          </div>

          {/* 2. Input Section */}
          <div>
            <label
              htmlFor="cargo-share-label"
              className="block text-sm font-medium text-[#374151] mb-1.5"
            >
              Cargo Name <span className="text-[#EF4444]">*</span>
            </label>

            <div className="w-full saved-fleet-wrapper">
              <input
                className="w-full"
                id="cargo-share-label"
                value={shareLabel}
                onChange={(e) => setShareLabel(e.target.value)}
                placeholder="e.g., 50k MT Iron Ore (Team Shared)"
              />
            </div>

            <p className="mt-2 text-xs text-[#6B7280]">
              Ensure the name is unique so your team can find it easily.
            </p>
          </div>

        </div>
      </CustomAddModal>

      <CustomAddModal
        open={saveModal}
        handleClose={onCloseSaveCargo}
        onSubmitConfirm={onClickSaveCargoLabel}
        maxWidth="xs"
        saveLabel="Save"
        title="Save to Cargo Cargo Estimation"
        contentPaddingClass="!px-4 !py-2"
      >
        <SaveCargoLabel
          value={saveLabel}
          onChange={(e) => setSaveLabel(e.target.value)}
          placeholder="Enter estimate name"
          label="Estimate name"
          guidanceTitle="What will be saved"
          guidance="Create a Cargo Cargo estimate from this order's loading and discharging details. Find it in Cargo Cargo Estimation → My Cargo."
        />
      </CustomAddModal>



      <CustomAddModal
        open={saveModalCargoVessel}
        handleClose={onCloseCargoVessel}
        onSubmitConfirm={onClickSaveCargoVesselLabel}
        maxWidth="xs"
        saveLabel="Save"
        title="Save to Single Cargo Vessel Estimation"
        contentPaddingClass="!px-4 !py-2"
      >
        <SaveCargoLabel
          value={saveLabelCargoVessel}
          onChange={(e) => {
            setSaveLabelCargoVessel(e.target.value);
            setSaveLabelCargoVesselError(false);
          }}
          placeholder="Enter estimate name"
          label="Estimate name"
          guidanceTitle="What will be saved"
          guidance="Save the selected cargo and vessel as one estimate. It opens in a new tab; find it later in All Estimation."
          error={saveLabelCargoVesselError ? "Enter an estimate name to save." : ""}
        />
      </CustomAddModal>

      <CustomShowModal
        open={modelVessel}
        title={"Vessel List"}
        maxWidth="lg"
        saveLabel={"Create"}
        handleClose={onCloseSaveCargoCargoVessel}
        onSubmitConfirm={onSubmitConfirm}
      >
        <VesselList
          setLoader={setLoader}
          selectedQuery={selectedQuery}
          fleets={fleets}
          onChangeVesselPagination={onChangeVesselPagination}
          vesselFilters={vesselFilters}
          onCloseSaveCargoCargoVessel={onCloseSaveCargoCargoVessel}
          hasFetchedFleets={hasFetchedFleets}
          onClickVessel={onClickVessel}
          selectedVessel={selectedVessel}
          onCreateVesselClick={onCreateVesselClick}
        />
      </CustomShowModal>

      <CustomShowModal
        open={isCargoAnalysisStepper}
        title={"Cargo Analysis"}
        maxWidth="lg"
        saveLabel={"Create"}
        // addThirdBtn={isStepper}
        // thirdBtnAction={onClickCreateCopy}
        // thirdBtnLabel="Create New Order"
        handleClose={onCloseModal}
        isDetailView={isDetailView}
        onSubmitConfirm={onSubmitConfirm}
        isDowloadPdf
      >
        <CreateCargoAnalysis
          // ref={addQueryModalRef}
          setLoader={setLoader}
          analysisVessel={analysisVessel}
          cargoAnalysisData={cargoAnalysisData}
          setCargoAnalysisData={setCargoAnalysisData}
          onCloseModal={onCloseModal}
          selectedQuery={selectedQuery}
          selectedPorts={selectedPorts}
          selectedPortsCommence={selectedPortsCommence}
          isEdit={isCargoAnalysisStepper}
          onChangePinQuery={onChangePinQuery}
          isDetailView={isDetailView}
          setIsEdit={setCargoAnalysisStepper}
        />
      </CustomShowModal>

      <CustomAddModal
        open={modelCargoContract}
        title={"Cargo Contract"}
        maxWidth="lg"
        saveLabel={"Create"}
        handleClose={handleCloseCargoContractModel}
        onSubmitConfirm={handleSubmit}
      >
        <CreateCargoContractModel
          formData={formData}
          setFormData={setFormData}
          handleChange={handleContractInputChange}
          removeAttachment={removeAttachment}
          textContract={textContract}
          handleTextContractUpload={handleTextContractUpload}
          handleAttachmentUpload={handleAttachmentUpload}
          attachments={attachments}
          setTextContract={setTextContract}
        />
      </CustomAddModal>

      <CustomConfirmModal
        open={warningPopup}
        handleClose={() => setWarningPopup(false)}
        onSubmitConfirm={() => {
          if (addQueryModalRef.current.onSubmitQuery) {
            addQueryModalRef.current.onSubmitQuery();
          }
          setWarningPopup(false);
        }}
        title="Cargo Update Confirmation"
        message="Are you sure you want to update cargo details?"
        note="Please double check all the details before submitting the update."
        cancelText="Cancel"
        confirmText="Update"
        type="info"
      />

      <CustomConfirmModal
        open={warningPopupForCopy}
        handleClose={() => setWarningPopupForCopy(false)}
        onSubmitConfirm={() => {
          if (addQueryModalRef.current.onSubmitQuery) {
            addQueryModalRef.current.onSubmitQuery(true);
          }
          setWarningPopupForCopy(false);
        }}
        title="Create New Order"
        message="Are you sure you want to create a copy of this order?"
        note="This action will add a new copy of this order to your orders list."
        cancelText="Cancel"
        confirmText="Confirm"
        type="info"
      />



      {isTooltipVisible && isTargetReady && steps[tourStep] && (
        <SpotlightOverlay
          targetSelector={steps[tourStep].target}
          active={isTooltipVisible}
          message={steps[tourStep].actionRequired && formErrors.length ? steps[tourStep].invalidMessage : steps[tourStep].message}
          onDismiss={handleTourDismiss}
          onNext={nextTourStep}
          onSkip={tourStep === steps.length - 1 ? null : () => handleTourComplete(true)}
          onTargetClick={() => {
            handleTourAction(steps[tourStep].key);
          }}
          position={steps[tourStep].position}
          targetPadding={2}
          targetRadius={6}
          nextLabel={steps[tourStep].actionRequired ? (formErrors.length ? "Finish" : "Next") : "Next"}
        />
      )}

      <GuideSpotlight tour={matrixTour} />
      {showVesselCard && (
        <GuideContinueCard
          title={allInMatrix ? "Every order is already in the matrix" : "Cargo step complete"}
          text={
            (allInMatrix ? "The Add to Matrix steps run on an order that isn't in the matrix yet, so create a new order to see them again. " : "") +
            (isGuideDone("vessel")
              ? "Open the matrix to see your cargo and vessel together."
              : "Your order is in the matrix. Now move to the vessel step: add a vessel and show it in the matrix too.")
          }
          label={isGuideDone("vessel") ? "Open matrix" : "Continue"}
          guideKey={isGuideDone("vessel") ? "view" : "vessel"}
          path={isGuideDone("vessel") ? undefined : "/vesselManager?tab=addVessel&subTab=1"}
          onClose={() => setShowVesselCard(false)}
        />
      )}
    </div>
  );
}

export default Orders;
